import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';

import { Alert, Box, Button, Paragraph, TextButton, Toast, useToast } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import { logError } from '@zg-rentals/log-error';

import { useSelector } from '../../../../selectors/useSelector';

import ListingAvailabilityModal from '../../../Modal/ListingAvailabilityModal';
import { useConfirmAvailability } from '../../../../hooks/queries/availability';
import PropertiesActions from '../../../../actions/PropertiesActions';

import type { IAPIErrorResponse } from '../../../../interfaces';

const isValidTimestamp = (timestamp: unknown): timestamp is string | number => {
  if (timestamp == null) return false;
  const num = Number(timestamp);
  return !Number.isNaN(num);
};

const ListingAvailability = ({
  expirationDateTimestamp: propExpirationDateTimestamp,
}: {
  expirationDateTimestamp?: string | number;
}) => {
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();

  const {
    currentProperty: { expirationDateTimestamp, listingAlias },
  } = useSelector((state) => state.properties);

  const [displayLearnMoreModal, setDisplayLearnMoreModal] = useState(false);

  const { mutate: confirmListingAvailability } = useConfirmAvailability(listingAlias, {
    onError: (err: IAPIErrorResponse) => {
      if (displayLearnMoreModal) {
        setDisplayLearnMoreModal(false);
      }
      enqueueToast(
        <Toast tone="critical">
          {err?.error?.message || 'An error occurred while confirming your listing availability'}
        </Toast>,
        {
          duration: 10000,
        },
      );
    },
    onSuccess: (response: string) => {
      dispatch(
        PropertiesActions.updateCurrentProperty({
          expirationDateTimestamp: response,
        }),
      );
      queryClient.invalidateQueries({ queryKey: ['getPropertyDetailsOverview', listingAlias] });

      if (displayLearnMoreModal) {
        setDisplayLearnMoreModal(false);
      }

      enqueueToast(
        <Toast tone="success">
          {`Thanks for confirming! We'll keep your listing active until ${format(
            new Date(Number(response)),
            'MM/dd/yy',
          )}`}
        </Toast>,
        {
          duration: 10000,
        },
      );
    },
  });

  const handleLearnMoreClick = () => {
    setDisplayLearnMoreModal(true);
  };

  // Guard against invalid timestamp values that would cause date-fns format() to throw
  if (!isValidTimestamp(expirationDateTimestamp)) {
    logError({
      error: new Error('Invalid time value'),
      context: {
        expirationDateTimestamp,
        propExpirationDateTimestamp,
        type: typeof expirationDateTimestamp,
        asNumber: Number(expirationDateTimestamp),
        listingAlias,
      },
    });
    return null;
  }

  return (
    <Alert tone="warning">
      <Box
        css={{
          display: 'flex',
          flexDirection: { base: 'column', xl: 'row' },
          alignItems: { base: 'flex-start', xl: 'center' },
          justifyContent: { base: 'flex-start', xl: 'space-between' },
        }}
      >
        <Box>
          <Paragraph textStyle="body-bold">Is this listing still available?</Paragraph>
          <Paragraph textStyle="body" css={{ marginBottom: { base: '200', xl: 0 } }}>
            {`Listing expires ${format(new Date(Number(expirationDateTimestamp)), 'MMM dd, yyyy')}`}
          </Paragraph>
        </Box>

        <Box
          css={{
            display: 'flex',
            flexDirection: { base: 'column', xl: 'row' },
            alignItems: { base: 'flex-start', xl: 'center' },
          }}
        >
          <Box css={{ display: 'flex', order: { base: 1, xl: 2 } }}>
            <Button
              css={{ marginBottom: { lgDown: '200' }, marginRight: { lgDown: '400' } }}
              tone="brand"
              emphasis="filled"
              size="sm"
              onClick={() => confirmListingAvailability()}
            >
              Confirm availability
            </Button>
          </Box>
          <Box css={{ display: 'flex', order: { base: 2, xl: 1 } }}>
            <TextButton onClick={handleLearnMoreClick} textStyle="body-sm-bold" css={{ marginRight: { xl: '400' } }}>
              Learn more
            </TextButton>
          </Box>
        </Box>
      </Box>

      {displayLearnMoreModal && (
        <ListingAvailabilityModal
          onConfirmClick={confirmListingAvailability}
          onModalClose={() => setDisplayLearnMoreModal(false)}
        />
      )}
    </Alert>
  );
};

export default ListingAvailability;
